import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, MessageSquare, CheckCircle, Clock, X, Send, Image as ImageIcon, 
  ExternalLink, AlertCircle, Phone, Mail, User, ShieldAlert, ShieldCheck, 
  Flag, Ban, AlertTriangle, Eye, Lock, RefreshCw, ThumbsUp, VolumeX
} from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';
import { CountryBadge } from '../components/SearchableCountrySelect';
import { chatModerationAPI } from '../utils/api';

const priorityConfig = {
  Urgent: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', dot: 'bg-rose-500' },
  High: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', dot: 'bg-orange-500' },
  Medium: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', dot: 'bg-amber-500' },
  Low: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', dot: 'bg-blue-500' }
};

const SupportCenter = () => {
  const [activeTab, setActiveTab] = useState('tickets'); // 'tickets' | 'chat_moderation'

  // Tickets State
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'open' | 'pending' | 'resolved'
  const [roleFilter, setRoleFilter] = useState('ALL'); // 'ALL' | 'MANAGER' | 'BD' | 'AGENCY' | 'HOST' | 'USER'
  const [previewImage, setPreviewImage] = useState(null);

  // Chat Moderation State (XOXO Lite Terms Section 5 & 9 Compliance)
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportStatusFilter, setReportStatusFilter] = useState('ALL'); // 'ALL' | 'pending' | 'resolved' | 'dismissed'
  const [moderatorNote, setModeratorNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState({ text: '', type: '' });

  const showToast = (text, type = 'success') => {
    setToastMessage({ text, type });
    setTimeout(() => setToastMessage({ text: '', type: '' }), 4000);
  };

  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchTickets();
    } else if (activeTab === 'chat_moderation') {
      fetchChatReports();
    }
  }, [activeTab, reportStatusFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/support/tickets`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setTickets(response.data.data);
        if (selectedTicket) {
          const updated = response.data.data.find(t => t.id === selectedTicket.id);
          setSelectedTicket(updated || null);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatReports = async () => {
    setReportsLoading(true);
    try {
      const res = await chatModerationAPI.getReports({ status: reportStatusFilter });
      if (res.data?.success) {
        setReports(res.data.data || []);
        if (selectedReport) {
          const updated = (res.data.data || []).find(r => r.id === selectedReport.id);
          setSelectedReport(updated || null);
        }
      }
    } catch (err) {
      console.error("Error fetching chat reports:", err);
      showToast("Failed to load chat reports", "error");
    } finally {
      setReportsLoading(false);
    }
  };

  const handleModerationAction = async (actionType) => {
    if (!selectedReport) return;
    setActionLoading(true);
    try {
      const res = await chatModerationAPI.takeAction(selectedReport.id, {
        action: actionType,
        notes: moderatorNote
      });
      if (res.data?.success) {
        showToast(`Report marked as ${actionType}!`, 'success');
        setModeratorNote('');
        fetchChatReports();
      }
    } catch (err) {
      console.error("Error taking moderation action:", err);
      showToast(err.response?.data?.message || "Failed to process action", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;
    setReplyLoading(true);
    try {
      const res = await axios.post(`${constant.API_BASE_URL}/admin/business/support/tickets/${selectedTicket.id}/reply`, {
        message: replyText
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setReplyText('');
        fetchTickets();
      }
    } catch (err) {
      alert("Failed to submit reply");
    } finally {
      setReplyLoading(false);
    }
  };

  const handleResolveTicket = async (id, status = 'Resolved') => {
    try {
      setSelectedTicket(prev => prev && prev.id === id ? { ...prev, status } : prev);
      setTickets(prev => prev.map(t => t.id === id ? { ...t, status } : t));

      await axios.put(`${constant.API_BASE_URL}/admin/business/support/tickets/${id}/status`, { status }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      await axios.post(`${constant.API_BASE_URL}/admin/business/support/tickets/${id}/reply`, {
        message: `[Support Notice] Status marked as: ${status}`,
        status
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      fetchTickets();
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    }
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      // 1. Status Filter
      if (statusFilter !== 'ALL') {
        const s = (t.status || '').toLowerCase();
        if (statusFilter === 'resolved' && (s !== 'resolved' && s !== 'solved')) return false;
        if (statusFilter === 'pending' && (s !== 'pending' && s !== 'in progress' && s !== 'inprogress')) return false;
        if (statusFilter === 'open' && s !== 'open') return false;
      }

      // 2. Role Filter (Manager to Admin, BD, Agency, Host, User)
      if (roleFilter !== 'ALL') {
        const ut = (t.user_type || 'USER').toUpperCase();
        if (roleFilter === 'MANAGER' && ut !== 'MANAGER') return false;
        if (roleFilter === 'BD' && ut !== 'BD') return false;
        if (roleFilter === 'AGENCY' && ut !== 'AGENCY') return false;
        if (roleFilter === 'HOST' && ut !== 'HOST') return false;
        if (roleFilter === 'USER' && (ut !== 'USER' && ut !== 'APP_USER')) return false;
      }

      // 3. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const ticketId = (t.ticket_id || '').toLowerCase();
        const subject = (t.subject || '').toLowerCase();
        const category = (t.category || '').toLowerCase();
        const msg = (t.message || '').toLowerCase();
        const userName = `${t.first_name || ''} ${t.last_name || ''}`.toLowerCase();
        const xoxoId = String(t.xoxo_id || t.app_user_id || '').toLowerCase();
        const contact = (t.contact || t.email || '').toLowerCase();

        return ticketId.includes(q) || 
               subject.includes(q) || 
               category.includes(q) || 
               msg.includes(q) || 
               userName.includes(q) || 
               xoxoId.includes(q) || 
               contact.includes(q);
      }
      return true;
    });
  }, [tickets, statusFilter, roleFilter, searchQuery]);

  const parseEvidenceMessages = (evidence) => {
    if (!evidence) return [];
    if (typeof evidence === 'string') {
      try { return JSON.parse(evidence); } catch (_) { return []; }
    }
    return Array.isArray(evidence) ? evidence : [];
  };

  const resolveImgUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http://') || img.startsWith('https://')) return img;
    const baseOrigin = constant.API_BASE_URL ? constant.API_BASE_URL.replace(/\/api$/, '') : 'https://admin.xoxolite.com/xoxo';
    const cleanPath = img.startsWith('/') ? img : `/${img}`;
    return `${baseOrigin}${cleanPath}`;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Toast Notification */}
      {toastMessage.text && (
        <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-2xl shadow-2xl text-white font-bold text-sm flex items-center gap-2 animate-bounce ${
          toastMessage.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          <CheckCircle size={18} /> {toastMessage.text}
        </div>
      )}

      {/* Top Header & Tab Navigation */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">Support & Safety Center</h2>
            <span className="px-3 py-0.5 rounded-full text-xs font-black bg-pink-100 text-pink-700">
              Live Desk
            </span>
          </div>
          <p className="text-slate-500 mt-1 text-sm">
            Review user support tickets, monitor chat safety reports, and enforce platform developer security terms.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-2 bg-slate-100 p-1.5 rounded-2xl w-full sm:w-auto">
          <button
            onClick={() => setActiveTab('tickets')}
            className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'tickets'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <MessageSquare size={15} className={activeTab === 'tickets' ? 'text-pink-600' : ''} />
            Support Tickets
          </button>
          <button
            onClick={() => setActiveTab('chat_moderation')}
            className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center justify-center gap-2 cursor-pointer ${
              activeTab === 'chat_moderation'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <ShieldAlert size={15} className={activeTab === 'chat_moderation' ? 'text-amber-500' : ''} />
            Chat Moderation & Reports
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: SUPPORT TICKETS */}
      {/* ========================================================================= */}
      {activeTab === 'tickets' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Ticket List Column */}
          <div className="lg:col-span-1 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden flex flex-col h-[700px]">
            <div className="p-4 border-b border-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-800">Support Tickets</h3>
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-extrabold text-xs">
                  {filteredTickets.length}
                </span>
              </div>
              <button onClick={fetchTickets} className="text-xs text-pink-600 font-bold hover:underline cursor-pointer">
                Refresh
              </button>
            </div>

            {/* Search Box */}
            <div className="p-3 border-b border-slate-100 bg-slate-50/50">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-3 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search ticket ID, user, subject..."
                  className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-pink-500/20 transition font-medium"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600">
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Filter Pills */}
            <div className="p-3 border-b border-slate-100 flex flex-wrap gap-1.5 bg-white">
              {['ALL', 'open', 'pending', 'resolved'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition cursor-pointer ${
                    statusFilter === status
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>

            {/* Ticket List Body */}
            <div className="flex-1 overflow-y-auto divide-y divide-slate-50 p-2 space-y-1">
              {loading ? (
                <div className="p-8 text-center text-slate-400 text-xs font-bold animate-pulse">Loading tickets...</div>
              ) : filteredTickets.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-xs font-bold">No tickets found</div>
              ) : (
                filteredTickets.map(ticket => {
                  const isSelected = selectedTicket?.id === ticket.id;
                  const isResolved = (ticket.status || '').toLowerCase() === 'resolved';

                  return (
                    <div
                      key={ticket.id}
                      onClick={() => setSelectedTicket(ticket)}
                      className={`p-3.5 rounded-2xl cursor-pointer transition flex flex-col gap-2 ${
                        isSelected 
                          ? 'bg-pink-50/80 border border-pink-200/60 shadow-xs' 
                          : 'hover:bg-slate-50/80 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[10px] font-black text-slate-400">
                          #{ticket.ticket_id ? String(ticket.ticket_id).substring(0, 8) : 'TICK'}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider ${
                            isResolved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {ticket.status || 'Open'}
                          </span>
                        </div>
                      </div>

                      <div className="font-extrabold text-xs text-slate-800 line-clamp-1">
                        {ticket.subject || ticket.category || 'General Support Ticket'}
                      </div>

                      <div className="flex items-center justify-between text-[11px] text-slate-500 font-medium">
                        <div className="flex items-center gap-1.5">
                          <User size={12} className="text-slate-400" />
                          <span className="line-clamp-1">{ticket.first_name || 'User'}</span>
                        </div>
                        <span>{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : ''}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Ticket Inspection & Chat Column */}
          <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden flex flex-col h-[700px]">
            {selectedTicket ? (
              <>
                {/* Header */}
                <div className="p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-pink-500 to-purple-600 flex items-center justify-center text-white font-black text-sm">
                      {selectedTicket.first_name ? selectedTicket.first_name[0] : 'U'}
                    </div>
                    <div>
                      <h3 className="font-extrabold text-sm text-slate-900 flex items-center gap-2">
                        {selectedTicket.subject || 'Support Request'}
                        <CountryBadge country={selectedTicket.country} />
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        User: <span className="font-bold text-slate-700">{selectedTicket.first_name} {selectedTicket.last_name || ''}</span> • 
                        ID: <span className="font-mono font-bold text-slate-600">{selectedTicket.xoxo_id || selectedTicket.app_user_id || 'N/A'}</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleResolveTicket(selectedTicket.id, 'Resolved')}
                      className="px-3.5 py-1.5 rounded-xl bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 text-xs font-black transition flex items-center gap-1.5 cursor-pointer"
                    >
                      <CheckCircle size={14} /> Resolve
                    </button>
                  </div>
                </div>

                {/* Messages Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/30">
                  {/* Initial Ticket Message */}
                  <div className="p-4 rounded-2xl bg-white border border-slate-100 shadow-xs space-y-2">
                    <span className="text-[10px] font-black uppercase text-pink-600 tracking-wider">Initial User Message</span>
                    <p className="text-xs text-slate-700 leading-relaxed font-medium">{selectedTicket.message}</p>
                    {selectedTicket.images && (
                      <div className="pt-2">
                        <img 
                          src={resolveImgUrl(selectedTicket.images)} 
                          alt="Attachment" 
                          onClick={() => setPreviewImage(resolveImgUrl(selectedTicket.images))}
                          className="w-32 h-32 object-cover rounded-xl cursor-pointer hover:opacity-80 transition border border-slate-200" 
                        />
                      </div>
                    )}
                  </div>

                  {/* Reply Threads */}
                  {selectedTicket.replies && selectedTicket.replies.map((reply, i) => {
                    const senderName = reply.sender_name || reply.sender || 'Support Team';
                    const isAdmin = senderName.toLowerCase().includes('admin') || senderName.toLowerCase().includes('support') || reply.sender === 'System';
                    const timeStr = reply.time || reply.timestamp;

                    return (
                      <div key={i} className={`flex flex-col max-w-[85%] ${isAdmin ? 'ml-auto items-end' : 'items-start'}`}>
                        <div className={`p-4 rounded-2xl shadow-sm border ${
                          isAdmin ? 'bg-slate-900 text-white border-slate-900 rounded-tr-none' : 'bg-white text-slate-800 border-slate-100 rounded-tl-none'
                        }`}>
                          <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap">{reply.message}</p>
                          <span className={`text-[10px] mt-2 block ${isAdmin ? 'text-slate-400' : 'text-slate-400'}`}>
                            {senderName} • {timeStr ? new Date(timeStr).toLocaleString() : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Reply Footer */}
                <form onSubmit={handleSendReply} className="p-4 border-t border-slate-100 flex gap-3 shrink-0 bg-white">
                  <input
                    required
                    placeholder="Type official support reply to the user..."
                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition text-sm font-medium"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <button
                    type="submit"
                    disabled={replyLoading}
                    className="px-6 py-3 bg-pink-600 hover:bg-pink-700 active:scale-95 text-white font-bold rounded-xl transition flex items-center justify-center gap-2 shadow-lg shadow-pink-200 disabled:opacity-50 cursor-pointer"
                  >
                    <Send size={16} /> Send Reply
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400 p-8">
                <MessageSquare size={54} className="opacity-20 mb-3" />
                <h4 className="font-extrabold text-slate-600 text-lg">No Ticket Selected</h4>
                <p className="text-sm mt-1 max-w-sm">Select any user ticket from the queue on the left to inspect logs, view screenshots, and send official replies.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: CHAT MODERATION & REPORTS (XOXO LITE TERMS SECTION 5 & 9 COMPLIANCE) */}
      {/* ========================================================================= */}
      {activeTab === 'chat_moderation' && (
        <div className="space-y-6">
          
          {/* Section 9 Privacy Banner */}
          <div className="bg-gradient-to-r from-amber-500/10 via-purple-500/10 to-pink-500/10 border border-amber-200/60 p-4 rounded-2xl flex items-start gap-3 shadow-xs">
            <Lock className="text-amber-600 shrink-0 mt-0.5" size={20} />
            <div className="text-xs text-slate-700 leading-relaxed">
              <span className="font-black text-amber-900 uppercase tracking-wider block mb-0.5">
                🔒 Privacy & Security Terms Enforcement (Section 9 Compliance)
              </span>
              Under XOXO Lite Developer Terms & Conditions, all 1-on-1 private chats are confidential between sender and receiver. 
              Staff and administrators do not have access to general user chats. Moderation staff can only review reports and inspect the 
              specific messages submitted by users as violation evidence. All moderation actions are securely audit-logged.
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Reports List Column */}
            <div className="lg:col-span-1 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden flex flex-col h-[700px]">
              <div className="p-4 border-b border-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-800">Chat Violation Reports</h3>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-extrabold text-xs">
                    {reports.length}
                  </span>
                </div>
                <button onClick={fetchChatReports} className="text-xs text-amber-600 font-bold hover:underline cursor-pointer flex items-center gap-1">
                  <RefreshCw size={12} /> Refresh
                </button>
              </div>

              {/* Status Filter */}
              <div className="p-3 border-b border-slate-100 flex flex-wrap gap-1.5 bg-slate-50/50">
                {['ALL', 'pending', 'resolved', 'dismissed'].map(status => (
                  <button
                    key={status}
                    onClick={() => setReportStatusFilter(status)}
                    className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition cursor-pointer ${
                      reportStatusFilter === status
                        ? 'bg-slate-900 text-white shadow-xs'
                        : 'bg-white text-slate-600 hover:bg-slate-200 border border-slate-200'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>

              {/* Reports List Body */}
              <div className="flex-1 overflow-y-auto divide-y divide-slate-50 p-2 space-y-1">
                {reportsLoading ? (
                  <div className="p-8 text-center text-slate-400 text-xs font-bold animate-pulse">Loading reports...</div>
                ) : reports.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-xs font-bold">No chat violation reports found</div>
                ) : (
                  reports.map(report => {
                    const isSelected = selectedReport?.id === report.id;
                    const isPending = (report.status || 'pending').toLowerCase() === 'pending';

                    return (
                      <div
                        key={report.id}
                        onClick={() => setSelectedReport(report)}
                        className={`p-3.5 rounded-2xl cursor-pointer transition flex flex-col gap-2 ${
                          isSelected 
                            ? 'bg-amber-50/80 border border-amber-200/60 shadow-xs' 
                            : 'hover:bg-slate-50/80 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[10px] font-black text-amber-700 bg-amber-100 px-2 py-0.5 rounded-md">
                            {report.reason || 'Violation Report'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider ${
                            isPending ? 'bg-amber-500 text-white animate-pulse' : 'bg-slate-100 text-slate-700'
                          }`}>
                            {report.status}
                          </span>
                        </div>

                        <div className="text-xs text-slate-800 font-extrabold flex items-center gap-1.5">
                          <Flag size={12} className="text-red-500 shrink-0" />
                          Reported User: <span className="text-red-600">{report.reported_name || 'User'}</span>
                        </div>

                        <div className="flex items-center justify-between text-[11px] text-slate-500 font-medium">
                          <span>By: {report.reporter_name || 'Anonymous'}</span>
                          <span>{report.created_at ? new Date(report.created_at).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Evidence & Action Column */}
            <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden flex flex-col h-[700px]">
              {selectedReport ? (
                <>
                  {/* Header */}
                  <div className="p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/50">
                    <div>
                      <h3 className="font-extrabold text-sm text-slate-900 flex items-center gap-2">
                        <ShieldAlert size={16} className="text-amber-600" />
                        Report Details: {selectedReport.reason}
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Reported ID: <span className="font-mono font-bold text-red-600">{selectedReport.reported_xoxo_id || selectedReport.reported_id}</span> • 
                        Status: <span className="font-bold uppercase text-slate-700">{selectedReport.status}</span>
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 font-mono">
                        {selectedReport.created_at ? new Date(selectedReport.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                  </div>

                  {/* Body with Evidence Snapshot */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-slate-50/30">
                    
                    {/* Reporter & Reported Cards */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-xs space-y-1">
                        <span className="text-[10px] font-black uppercase text-slate-400">Reporter</span>
                        <div className="font-extrabold text-xs text-slate-800">{selectedReport.reporter_name || 'User'}</div>
                        <div className="text-[11px] text-slate-500 font-mono">UID: {selectedReport.reporter_xoxo_id || selectedReport.reporter_id}</div>
                      </div>
                      <div className="p-4 bg-red-50/50 rounded-2xl border border-red-100 shadow-xs space-y-1">
                        <span className="text-[10px] font-black uppercase text-red-500">Reported Accused</span>
                        <div className="font-extrabold text-xs text-red-700">{selectedReport.reported_name || 'User'}</div>
                        <div className="text-[11px] text-red-600 font-mono">UID: {selectedReport.reported_xoxo_id || selectedReport.reported_id}</div>
                      </div>
                    </div>

                    {/* Reporter Description */}
                    <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-xs space-y-1">
                      <span className="text-[10px] font-black uppercase text-slate-400">Reporter Description</span>
                      <p className="text-xs text-slate-700 font-medium leading-relaxed">
                        {selectedReport.description || 'No additional comment provided by reporter.'}
                      </p>
                    </div>

                    {/* Evidence Snapshot (Reported Messages) */}
                    <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-xs space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase text-amber-600 tracking-wider flex items-center gap-1.5">
                          <Eye size={12} /> Evidence Messages Snapshot
                        </span>
                        <span className="text-[10px] text-slate-400 font-bold">Only reported messages shown</span>
                      </div>

                      <div className="space-y-2 max-h-56 overflow-y-auto p-2 bg-slate-50 rounded-xl">
                        {parseEvidenceMessages(selectedReport.evidence_messages).length === 0 ? (
                          <div className="text-center text-xs text-slate-400 py-4">No message snapshots attached to this report</div>
                        ) : (
                          parseEvidenceMessages(selectedReport.evidence_messages).map((msg, idx) => (
                            <div key={idx} className="p-3 bg-white rounded-xl border border-slate-200 text-xs space-y-1">
                              <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
                                <span>Sender: {String(msg.sender_id).substring(0, 8)}...</span>
                                <span>{msg.created_at ? new Date(msg.created_at).toLocaleTimeString() : ''}</span>
                              </div>
                              <p className="font-semibold text-slate-800">{msg.text || '[Media Attachment]'}</p>
                              {msg.media_url && (
                                <img src={resolveImgUrl(msg.media_url)} alt="Evidence" className="w-24 h-24 object-cover rounded-lg border mt-1" />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Moderation Actions */}
                    <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-xs space-y-3">
                      <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Moderation Decision & Action</span>
                      <textarea
                        rows={2}
                        placeholder="Add moderator notes or warning explanation..."
                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-amber-500/20 font-medium"
                        value={moderatorNote}
                        onChange={e => setModeratorNote(e.target.value)}
                      />

                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          disabled={actionLoading}
                          onClick={() => handleModerationAction('warn')}
                          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 active:scale-95 text-white rounded-xl text-xs font-black transition flex items-center gap-1.5 shadow-sm cursor-pointer disabled:opacity-50"
                        >
                          <AlertTriangle size={14} /> Send Official Warning
                        </button>
                        <button
                          disabled={actionLoading}
                          onClick={() => handleModerationAction('mute_chat')}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 active:scale-95 text-white rounded-xl text-xs font-black transition flex items-center gap-1.5 shadow-sm cursor-pointer disabled:opacity-50"
                        >
                          <VolumeX size={14} /> Mute Chat Access
                        </button>
                        <button
                          disabled={actionLoading}
                          onClick={() => handleModerationAction('ban_chat')}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 active:scale-95 text-white rounded-xl text-xs font-black transition flex items-center gap-1.5 shadow-sm cursor-pointer disabled:opacity-50"
                        >
                          <Ban size={14} /> Ban from Messaging
                        </button>
                        <button
                          disabled={actionLoading}
                          onClick={() => handleModerationAction('dismiss')}
                          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-700 rounded-xl text-xs font-black transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50 ml-auto"
                        >
                          <ThumbsUp size={14} /> Dismiss Report
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400 p-8">
                  <ShieldAlert size={54} className="opacity-20 mb-3" />
                  <h4 className="font-extrabold text-slate-600 text-lg">No Report Selected</h4>
                  <p className="text-sm mt-1 max-w-sm">Select any user violation report from the queue on the left to review evidence and take moderation actions.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-3xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <img src={previewImage} alt="Full preview" className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl" />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-3 -right-3 p-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition shadow-lg"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportCenter;
